/**
 * MarkupOverlay — the read-only drawing over a photo, for everyone who can see the photo (office
 * and techs; Nick: "anyone can see the drawing"). Rendered inside the viewer's overlay slot, which
 * carries the photo's rotate/zoom, so the marks stay on the features they point at.
 *
 * NEVER CUSTOMER-FACING (allow-listed in scripts/guards/check-markup-isolation.mjs).
 */
import { useRef } from 'react';
import type { MarkupShape } from '../../../../types/fieldService/generatorPhotoMarkupVocab';
import { describeDrawing } from './markupGeometry';
import { MarkupShapes } from './MarkupShapes';
import { useBoxSize } from './useBoxSize';

interface MarkupOverlayProps {
  shapes: MarkupShape[];
  /** True when the photo's bytes changed since the drawing was saved (contentSha256 differs). */
  photoChanged?: boolean;
}

export function MarkupOverlay({ shapes, photoChanged = false }: MarkupOverlayProps) {
  const ref = useRef<SVGSVGElement>(null);
  const { w, h } = useBoxSize(ref);
  if (shapes.length === 0) return null;
  return (
    <>
      <svg
        ref={ref}
        viewBox={`0 0 ${w} ${h}`}
        className="absolute inset-0 h-full w-full pointer-events-none"
        role="img"
        aria-label={describeDrawing(shapes)}
        data-testid="markup-overlay"
      >
        <MarkupShapes shapes={shapes} w={w} h={h} />
      </svg>
      {photoChanged && (
        <p className="absolute left-2 top-2 rounded-lg bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-800 ring-1 ring-amber-200">
          This photo changed after it was drawn on — marks may be off.
        </p>
      )}
    </>
  );
}
